import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { primitiveStyles } from '../shared/primitive-utils.js';

/**
 * Composable content surface with independently optional media, header, actions and footer.
 * @element qds-card
 * @attr {string} label - Accessible name for the card article, when useful.
 * @property {string} label - Accessible article name; empty leaves content to supply context.
 * @slot - Arbitrary body content, including native lists, forms and other components.
 * @slot media - Optional image or other media; provide its own accessible alternative.
 * @slot header - Heading or other header content; choose a heading level for the page.
 * @slot actions - Header actions with their own names and keyboard semantics.
 * @slot footer - Optional supplementary content or actions.
 * @csspart surface - Bordered article surface.
 * @csspart media - Media container.
 * @csspart header - Header and actions row.
 * @csspart body - Default content container.
 * @csspart footer - Footer container.
 */
export class QdsCard extends HTMLElement {
  static get observedAttributes(): string[] { return ['label']; }
  private _onSlotChange = (): void => this._sync();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: block; min-width: 0; }
      [part=surface] { height: 100%; overflow-wrap: anywhere; border: 1px solid var(--theme-border-primary, #e0e0e0);
        border-radius: var(--qds-card-radius, 8px); background: var(--theme-background-primary, white); }
      [part=header], [part=body], [part=footer] { padding: var(--qds-card-padding, 16px); }
      [part=header] { display: flex; align-items: start; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
      [part=header] slot[name=header] { flex: 1; min-width: 0; }
      [part=header] slot[name=actions] { flex: 0 0 auto; }
      [part=footer] { border-top: 1px solid var(--theme-border-primary, #e0e0e0); }
      ::slotted([slot=media]) { display: block; width: 100%; border-radius: var(--qds-card-radius, 8px) var(--qds-card-radius, 8px) 0 0; }
      ::slotted([slot=header]) { margin: 0; }
      @media (forced-colors: active) { [part=surface] { border-color: CanvasText; } }
    </style><article part="surface"><div part="media"><slot name="media"></slot></div><header part="header"><slot name="header"></slot><slot name="actions"></slot></header><div part="body"><slot></slot></div><footer part="footer"><slot name="footer"></slot></footer></article>`;
    loadBaseCSS();
  }

  connectedCallback(): void {
    upgradeProperty(this, 'label');
    this.shadowRoot!.addEventListener('slotchange', this._onSlotChange);
    this._sync();
  }
  disconnectedCallback(): void { this.shadowRoot!.removeEventListener('slotchange', this._onSlotChange); }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get label(): string { return this.getAttribute('label') || ''; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }

  private _hasContent(name: string): boolean {
    const slot = this.shadowRoot!.querySelector<HTMLSlotElement>(`slot[name="${name}"]`)!;
    return slot.assignedNodes({ flatten: true }).some(node => node.nodeType === Node.ELEMENT_NODE || !!node.textContent?.trim());
  }
  private _sync(): void {
    const surface = this.shadowRoot!.querySelector('article')!;
    if (this.label) surface.setAttribute('aria-label', this.label); else surface.removeAttribute('aria-label');
    this.shadowRoot!.querySelector<HTMLElement>('[part=media]')!.hidden = !this._hasContent('media');
    this.shadowRoot!.querySelector<HTMLElement>('[part=header]')!.hidden = !this._hasContent('header') && !this._hasContent('actions');
    this.shadowRoot!.querySelector<HTMLElement>('[part=footer]')!.hidden = !this._hasContent('footer');
  }
}
if (!customElements.get('qds-card')) customElements.define('qds-card', QdsCard);
